import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Image, Item, Icon, Button, Segment, Label, Dropdown, Rating, Card, Dimmer, Loader, Message } from 'semantic-ui-react';
import { StatusLabels } from '../../components/statusTools/StatusIndicators';
import { getPriorityColor, getStatusColor } from '../../constants/statusConfig';
import characterImage from '../../assets/shutterstock_2294275225-480x480.jpg';
import providerImage from '../../assets/shutterstock_2294366549-480x480.png';
import { fetchActorUsers, selectActorUsers, selectActorUsersStatus, selectActorUsersError } from '../../slices/voiceActorSlice';
import { fetchUserSignedUrlPath } from '../../utils/fetchSignedUrlByFolderV2';

import { AuthStdFooter } from "../../utils/auth-std-footer";

// Voice Actor Card Component
const VoiceActorCard = ({ actor, onViewProfile }) => {
  const navigate = useNavigate();

  // Get the user ID from the Redux store
  const userId = useSelector((state) => state.user.profile.data?.userId);

  // State to track if the profile image fails to load
  const [imageError, setImageError] = useState(false);
  // State for the signed URL
  const [signedUrl, setSignedUrl] = useState(null);
  // State to track loading state
  const [isLoading, setIsLoading] = useState(false);

  // Fetch signed URL when component mounts or actor changes
  useEffect(() => {
    const getSignedUrl = async () => {
      if (!userId || !actor.profilePictureUrl) return;

      // Check if this is an S3 URL that needs signing
      const isS3Url = actor.profilePictureUrl.includes('s3.') &&
        actor.profilePictureUrl.includes('amazonaws.com');

      if (isS3Url) {
        setIsLoading(true);
        try {
          const url = await fetchUserSignedUrlPath(userId, actor.profilePictureUrl);
          if (url) {
            setSignedUrl(url);
          }
        } catch (error) {
          console.error('Error fetching signed URL:', error);
          setImageError(true);
        } finally {
          setIsLoading(false);
        }
      }
    };

    getSignedUrl();
  }, [userId, actor.profilePictureUrl]);

  // Ensure the profile picture URL is secure (https)
  const ensureSecureUrl = (url) => {
    if (!url) return url;
    // If the URL starts with http://, convert it to https://
    return url.replace(/^http:\/\//i, 'https://');
  };

  // Determine which image URL to use
  const getImageUrl = () => {
    // If we have a signed URL, use it
    if (signedUrl) {
      return signedUrl;
    }

    // If the profile URL exists and is not an S3 URL (or we're still loading), use it with https
    if (!imageError && actor.profilePictureUrl) {
      // Check if this is an S3 URL
      const isS3Url = actor.profilePictureUrl.includes('s3.') &&
        actor.profilePictureUrl.includes('amazonaws.com');

      // If it's not an S3 URL or we're still loading, use the URL directly
      if (!isS3Url || isLoading) {
        return ensureSecureUrl(actor.profilePictureUrl);
      }
    }

    // Fall back to the default image
    return providerImage;
  };

  // Get the image URL to display
  const profileImage = getImageUrl();

  // Combine first name and last name for display
  const actorName = `${actor.firstName} ${actor.lastName}`;

  return (
    <Card fluid>
      <Card.Content>
        <div style={{ marginBottom: "70px", display: 'flex', alignItems: 'center' }}>
          {isLoading ? (
            <div style={{
              width: '80px',
              height: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '15px'
            }}>
              <Icon name="spinner" loading />
            </div>
          ) : (
            <Image
              src={profileImage}
              size='tiny'
              // circular
              style={{ marginRight: '15px', borderRadius: "8px" }}
              onError={() => setImageError(true)}
            />
          )}
          <div style={{ flex: 1 }}>
            <Card.Header as='a' onClick={() => onViewProfile(actor.uuid)} style={{ fontSize: '1.2rem', marginBottom: '5px' }}>
              {actorName}
            </Card.Header>
            <Card.Description style={{
              height: '40px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical'
            }}>
              {actor.description || 'No description available'}
            </Card.Description>
            <div style={{ marginTop: '10px' }}>
              <StatusLabels
                priority={actor.priority || "Medium"}
                status={actor.status || "New"}
                deadline={actor.deadline || null}
                getPriorityColor={getPriorityColor}
                getStatusColor={getStatusColor}
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
              <div>
                <Rating icon='star' defaultRating={actor.rating || 0} maxRating={5} disabled size='small' />
                <span style={{ marginLeft: '5px', fontSize: '0.9rem', color: '#666' }}>
                  ({actor.rating || 0}) · <a href="#reviews" onClick={(e) => { e.preventDefault(); onViewProfile(actor.uuid, 'reviews'); }}>
                    {actor.reviewCount || 0} reviews
                  </a>
                </span>
              </div>
              <a href="#more" onClick={(e) => { e.preventDefault(); onViewProfile(actor.uuid); }}>more...</a>
            </div>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
};

// Main Component
const VoiceActorsList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get voice actors from Redux store
  const voiceActors = useSelector(selectActorUsers);
  const status = useSelector(selectActorUsersStatus);
  const error = useSelector(selectActorUsersError);

  const [filteredActors, setFilteredActors] = useState([]);
  const [actorTypeFilter, setActorTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');

  // Fetch voice actors when component mounts
  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchActorUsers()).unwrap();
    }
  }, [status, dispatch]);

  // Update filtered actors when voice actors or filters change
  useEffect(() => {
    console.log('voiceActors', voiceActors);
    applyFilters(actorTypeFilter, statusFilter, priorityFilter);
  }, [voiceActors, actorTypeFilter, statusFilter, priorityFilter]);

  // Sort actors alphabetically by name
  const sortedActors = [...filteredActors].sort((a, b) => {
    const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
    const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
    return nameA.localeCompare(nameB);
  });

  // Filter handlers
  const handleActorTypeFilter = (e, { value }) => {
    setActorTypeFilter(value);
  };

  const applyFilters = (actorType, status, priority) => {
    let filtered = [...voiceActors];

    // Apply actor type filter
    if (actorType !== 'all') {
      filtered = filtered.filter(actor => actor.type === actorType);
    }

    // Apply status filter
    if (status !== 'all') {
      filtered = filtered.filter(actor => actor.status === status);
    }

    // Apply priority filter
    if (priority !== 'all') {
      filtered = filtered.filter(actor => actor.priority === priority);
    }

    setFilteredActors(filtered);
  };

  const handleViewProfile = (actorId, section = '') => {
    // Navigate to the voice actor worksheet with the selected actor ID
    navigate('/dashBoard', {
      state: {
        menuSelection: 'VOICE-ACTOR-WORKSHEET',
        actorId: actorId
      }
    });
  };

  return (
    <>
      {/* Header Section */}
      <div className='listContainer' style={{ marginTop: "30px", marginBottom: "20px" }}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={3}>
              <div className="img-with-gradient">
                <Image src={characterImage} />
              </div>
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">Voice Actors</span>
              <span className="blgListSubTitle">Find the perfect voice for your project</span>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </div>

      {/* Filtering Options */}
      <div className='listContainer'>
        <Segment basic>
          <Grid>
            <Grid.Row>
              <Grid.Column width={4}>
                <Dropdown
                  className='listItemDropDn'
                  placeholder='Filter by actor type'
                  fluid
                  selection
                  options={[
                    { key: 'all', text: 'All Voice Actors', value: 'all' },
                    { key: 'authorator', text: 'Authorator-ai Actors', value: 'Authorator-ai' },
                    { key: 'freelance', text: 'Freelance Actors', value: 'Freelance' },
                  ]}
                  onChange={handleActorTypeFilter}
                  value={actorTypeFilter}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <Dropdown
                  className='listItemDropDn'
                  placeholder='Filter by status'
                  fluid
                  selection
                  options={[
                    { key: 'all', text: 'All Statuses', value: 'all' },
                    { key: 'new', text: 'New', value: 'New' },
                    { key: 'in-progress', text: 'In Progress', value: 'In Progress' },
                    { key: 'completed', text: 'Completed', value: 'Completed' },
                  ]}
                  onChange={(e, { value }) => setStatusFilter(value)}
                  value={statusFilter}
                />
              </Grid.Column>
              <Grid.Column width={4}>
                <Dropdown
                  className='listItemDropDn'
                  placeholder='Filter by priority'
                  fluid
                  selection
                  options={[
                    { key: 'all', text: 'All Priorities', value: 'all' },
                    { key: 'high', text: 'High Priority', value: 'High' },
                    { key: 'medium', text: 'Medium Priority', value: 'Medium' },
                    { key: 'low', text: 'Low Priority', value: 'Low' },
                  ]}
                  onChange={(e, { value }) => setPriorityFilter(value)}
                  value={priorityFilter}
                />
              </Grid.Column>
              <Grid.Column width={4} textAlign="right">
                <Button
                  primary
                  onClick={() => navigate('/dashBoard', { state: { menuSelection: 'VOICE-ACTOR-WORKSHEET' } })}
                >
                  <Icon name='plus' />
                  Add New Voice Actor
                </Button>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>
      </div>

      {/* Voice Actors List */}
      <div className='listContainer'>
        <div className="subPageHeaderFont" style={{ marginBottom: '15px' }}>Voice Actor Directory</div>

        {/* Loading State */}
        {status === 'loading' && (
          <Segment>
            <Dimmer active inverted>
              <Loader inverted>Loading Voice Actors</Loader>
            </Dimmer>
            <Image src='https://react.semantic-ui.com/images/wireframe/short-paragraph.png' />
          </Segment>
        )}

        {/* Error State */}
        {status === 'failed' && (
          <Message negative>
            <Message.Header>Error Loading Voice Actors</Message.Header>
            <p>{error || 'An unknown error occurred'}</p>
          </Message>
        )}

        {/* Success State */}
        {status === 'succeeded' && (
          <>
            {sortedActors.length === 0 ? (
              <Message info>
                <Message.Header>No Voice Actors Found</Message.Header>
                <p>
                  {actorTypeFilter === 'all' && statusFilter === 'all' && priorityFilter === 'all'
                    ? 'There are no voice actors in the system. Click "Add New Voice Actor" to create one.'
                    : 'There are no voice actors matching the selected filters. Try changing the filters or add a new voice actor.'
                  }
                </p>
              </Message>
            ) : (
              <Grid style={{ marginTop: "10px" }}>
                <Grid.Row>
                  <Grid.Column>
                    <Grid columns={2}>
                      {sortedActors.map((actor) => (
                        <Grid.Column key={actor.uuid} style={{ marginBottom: '15px' }}>
                          <VoiceActorCard
                            actor={actor}
                            onViewProfile={handleViewProfile}
                          />
                        </Grid.Column>
                      ))}
                    </Grid>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            )}
          </>
        )}
        {AuthStdFooter()}
      </div>


    </>
  );
};

export default VoiceActorsList;
